import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AmenityReturn,
  ReservationPreviewReturn,
  ReservationReturn,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Input } from '../Components/Catalyst/input';
import { Textarea } from '../Components/Catalyst/textarea';
import { Checkbox, CheckboxField } from '../Components/Catalyst/checkbox';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { formatHomeAddress, useMember } from '../Hooks/MemberContext';
import { useImpersonating } from '../Hooks/useImpersonating';
import { formatMoney } from '../utils/billingHelpers';
import {
  amenityHasFee,
  formatLocalDateTime,
  formatLocalRange,
  isFreeAmount,
  nightsLabel,
  showPartySize,
  timesAreIn,
} from './reservationFormat';
import {
  reservationErrorMessage,
  ruleMessage,
  violationsFromError,
} from './ruleMessages';
import TimePicker, { PickedTime } from './TimePicker';
import {
  depositReviewText,
  depositStatusText,
  documentRequirementLines,
  feeLineAmount,
  showFeeBreakdown,
} from './reservationPhase3';

interface Props {
  amenity: AmenityReturn;
  homeId: string;
}

type Step = 'pick' | 'review' | 'done';

/** Codes the review step answers itself, with the agreement checkbox. */
const AGREEMENT_CODES = ['AGREEMENT_REQUIRED', 'AGREEMENT_VERSION_STALE'];

/**
 * Booking: when (the shared `TimePicker`) → who and what for → review →
 * confirmed or requested (#643, #640 phase 2). Mobile-first: one column,
 * native date input, big tap targets.
 *
 * Every rule is the server's. This panel shows the grid the server
 * computed, asks it for a preview before the homeowner commits, and
 * treats a 422 on submit as the answer — including `QUOTE_CHANGED`,
 * which sends the homeowner back to review the new price.
 */
const BookingPanel: React.FC<Props> = ({ amenity, homeId }) => {
  const { homes } = useMember();
  const impersonating = useImpersonating();
  const home = homes.find((h) => h.home_id === homeId);
  const overnight = amenity.booking_mode === 'overnight';

  const [step, setStep] = useState<Step>('pick');
  const [picked, setPicked] = useState<PickedTime | null>(null);
  const [partySize, setPartySize] = useState<string>('1');
  const [purpose, setPurpose] = useState('');
  const [plate, setPlate] = useState('');
  const [plateState, setPlateState] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [preview, setPreview] = useState<ReservationPreviewReturn | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const [priceChanged, setPriceChanged] = useState(false);
  const [booked, setBooked] = useState<ReservationReturn | null>(null);
  const [reloadDay, setReloadDay] = useState(0);

  // A different home can mean a different household's limits; start over.
  useEffect(() => {
    setStep('pick');
    setPicked(null);
    setPreview(null);
    setBooked(null);
  }, [homeId, amenity.id]);

  const start = picked?.start ?? null;
  const end = picked?.end ?? null;
  const party = Math.max(1, parseInt(partySize, 10) || 1);
  // A shared slot takes no more than is left in it; the server checks too.
  const partyMax =
    picked?.remaining !== null && picked?.remaining !== undefined
      ? Math.min(picked.remaining, amenity.max_party_size ?? picked.remaining)
      : amenity.max_party_size ?? undefined;
  const vehicleMissing = amenity.requires_vehicle && plate.trim().length === 0;

  const requestBody = (agreementId?: string | null, quotedFee?: string) => ({
    amenity_id: amenity.id,
    start_local: start as string,
    end_local: end as string,
    party_size: party,
    purpose: purpose.trim() || null,
    agreement_id: agreementId ?? null,
    quoted_fee: quotedFee ?? null,
    vehicle_plate: amenity.requires_vehicle ? plate.trim() || null : null,
    vehicle_state: amenity.requires_vehicle ? plateState.trim() || null : null,
    vehicle_description: amenity.requires_vehicle ? vehicle.trim() || null : null,
  });

  const runPreview = async (): Promise<void> => {
    if (!start || !end) return;
    setWorking(true);
    setError(null);
    setViolations([]);
    try {
      const { data } = await memberReservationService.previewMyReservation({
        homeId,
        memberReservationCreate: requestBody(amenity.current_agreement?.id ?? null),
      });
      setPreview(data);
      setAgreed(false);
      setStep('review');
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't check that booking."));
    } finally {
      setWorking(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (!preview || !start || !end) return;
    setWorking(true);
    setError(null);
    setViolations([]);
    setPriceChanged(false);
    try {
      const { data } = await memberReservationService.createMyReservation({
        homeId,
        memberReservationCreate: requestBody(
          amenity.requires_agreement ? (preview.agreement?.id ?? null) : null,
          preview.fee
        ),
      });
      setBooked(data);
      setStep('done');
    } catch (err) {
      const refused = violationsFromError(err);
      if (refused) {
        if (refused.some((v) => v.code === 'QUOTE_CHANGED')) {
          // Fetch the new price and make the homeowner look at it again.
          setPriceChanged(true);
          try {
            const { data } = await memberReservationService.previewMyReservation({
              homeId,
              memberReservationCreate: requestBody(preview.agreement?.id ?? null),
            });
            setPreview(data);
            setAgreed(false);
          } catch (e) {
            setError(reservationErrorMessage(e, "Couldn't check the new price."));
          }
        } else {
          setViolations(refused);
          if (refused.some((v) => v.code === 'SLOT_CONFLICT' || v.code === 'CAPACITY_EXCEEDED')) {
            setReloadDay((n) => n + 1);
          }
        }
      } else {
        setError(reservationErrorMessage(err, "Couldn't make that booking."));
      }
    } finally {
      setWorking(false);
    }
  };

  const reviewBlockers = (preview?.violations ?? []).filter(
    (v) => AGREEMENT_CODES.indexOf(v.code) === -1
  );
  const needsAgreement = amenity.requires_agreement && !!preview?.agreement;
  const what = overnight ? 'stay' : 'booking';
  const documentLines = documentRequirementLines(amenity);

  // ---------------------------------------------------------------------------

  if (step === 'done' && booked) {
    const pending = booked.status === 'pending';
    return (
      <section
        aria-live="polite"
        className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800"
      >
        <Subheading>{pending ? 'Requested — awaiting approval' : 'Confirmed'}</Subheading>
        <Text className="mt-2">
          {amenity.name} · {formatLocalRange(booked.start_local, booked.end_local)}
        </Text>
        <Text className="mt-2">
          {pending
            ? amenityHasFee(amenity)
              ? `Your management company will review your request. Nothing is charged unless it's approved — then ${formatMoney(booked.fee_amount)} goes on your account.`
              : "Your management company will review your request. We'll let you know when they do."
            : amenityHasFee(amenity)
              ? `${formatMoney(booked.fee_amount)} has been added to your account.`
              : "You're all set."}
        </Text>
        {depositStatusText(booked) && <Text className="mt-2">{depositStatusText(booked)}</Text>}
        {documentLines.length > 0 && (
          <Text className="mt-2">
            Next, upload the documents this booking needs from the booking page.
          </Text>
        )}
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={`/reservations/${booked.id}`}
            className="text-sm font-medium text-sky-700 hover:underline dark:text-sky-400"
          >
            View reservation
          </Link>
          <button
            type="button"
            className="text-sm text-zinc-600 hover:underline dark:text-zinc-400"
            onClick={() => {
              setStep('pick');
              setBooked(null);
              setPicked(null);
              setPreview(null);
              setReloadDay((n) => n + 1);
            }}
          >
            Book another time
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800">
      <Subheading>Book {amenity.name}</Subheading>
      <Text className="mt-1 text-sm">
        {timesAreIn(amenity.timezone)}
        {home ? ` · for ${formatHomeAddress(home)}` : ''}
      </Text>

      {impersonating && (
        <Text className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          This is a read-only view: you can check times and prices, but not book.
        </Text>
      )}

      {/* Kept mounted through review so going Back keeps the choice. */}
      <div className={step === 'pick' ? 'mt-4 space-y-5' : 'hidden'}>
        <TimePicker
          amenity={amenity}
          homeId={homeId}
          reloadKey={reloadDay}
          onChange={setPicked}
        />

        {picked && (
          <>
            {showPartySize(amenity) && (
              <Field>
                <Label>How many people</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={partyMax}
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                />
              </Field>
            )}
            {amenity.requires_vehicle && (
              <fieldset className="space-y-3">
                <legend className="text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">
                  Vehicle
                </legend>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                  <Field>
                    <Label>Licence plate</Label>
                    <Input
                      value={plate}
                      maxLength={16}
                      autoCapitalize="characters"
                      onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    />
                  </Field>
                  <Field>
                    <Label>State (optional)</Label>
                    <Input
                      value={plateState}
                      maxLength={16}
                      autoCapitalize="characters"
                      onChange={(e) => setPlateState(e.target.value.toUpperCase())}
                    />
                  </Field>
                </div>
                <Field>
                  <Label>Make, model and colour (optional)</Label>
                  <Input
                    value={vehicle}
                    maxLength={120}
                    placeholder="e.g. Blue Honda Civic"
                    onChange={(e) => setVehicle(e.target.value)}
                  />
                </Field>
              </fieldset>
            )}
            <Field>
              <Label>What's it for? (optional)</Label>
              <Textarea
                rows={2}
                value={purpose}
                maxLength={500}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder={overnight ? 'e.g. Visiting family' : 'e.g. Birthday party'}
              />
            </Field>
          </>
        )}

        {error && step === 'pick' && <ErrorAlert>{error}</ErrorAlert>}

        <Button
          color="sky"
          className="w-full sm:w-auto"
          disabled={!picked || working || vehicleMissing}
          onClick={runPreview}
        >
          {working ? 'Checking…' : `Review ${what}`}
        </Button>
      </div>

      {step === 'review' && preview && start && end && (
        <div className="mt-4 space-y-4">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-6">
            {overnight ? (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Check in</dt>
                <dd className="text-zinc-950 dark:text-white">{formatLocalDateTime(start)}</dd>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Check out</dt>
                <dd className="text-zinc-950 dark:text-white">{formatLocalDateTime(end)}</dd>
                {picked?.nights && (
                  <>
                    <dt className="font-medium text-zinc-500 dark:text-zinc-400">Nights</dt>
                    <dd className="text-zinc-950 dark:text-white">{nightsLabel(picked.nights)}</dd>
                  </>
                )}
              </>
            ) : (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">When</dt>
                <dd className="text-zinc-950 dark:text-white">{formatLocalRange(start, end)}</dd>
              </>
            )}
            {showPartySize(amenity) && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">People</dt>
                <dd className="text-zinc-950 dark:text-white">{party}</dd>
              </>
            )}
            {amenity.requires_vehicle && plate.trim() && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Vehicle</dt>
                <dd className="text-zinc-950 dark:text-white">
                  {[plate.trim(), plateState.trim()].filter(Boolean).join(' · ')}
                  {vehicle.trim() ? ` — ${vehicle.trim()}` : ''}
                </dd>
              </>
            )}
            {purpose.trim() && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">For</dt>
                <dd className="text-zinc-950 dark:text-white">{purpose.trim()}</dd>
              </>
            )}
            <dt className="font-medium text-zinc-500 dark:text-zinc-400">Fee</dt>
            <dd className="text-zinc-950 dark:text-white">
              {isFreeAmount(preview.fee)
                ? 'Free'
                : `${formatMoney(preview.fee)}${preview.status_if_booked === 'pending' ? ', charged to your account only if approved' : ', charged to your account'}`}
            </dd>
            {showFeeBreakdown(preview.fee_breakdown) && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">How it adds up</dt>
                <dd className="text-zinc-950 dark:text-white">
                  <ul className="space-y-0.5">
                    {preview.fee_breakdown.map((line, i) => (
                      <li key={`${line.kind}-${i}`} className="flex justify-between gap-4">
                        <span className="min-w-0">{line.label}</span>
                        <span className="shrink-0 tabular-nums">{feeLineAmount(line)}</span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            )}
            {depositReviewText(preview.deposit, preview.status_if_booked === 'pending') && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Deposit</dt>
                <dd className="text-zinc-950 dark:text-white">
                  {depositReviewText(preview.deposit, preview.status_if_booked === 'pending')}
                </dd>
              </>
            )}
            {amenityHasFee(amenity) && preview.fee_refundable_until_local && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Cancellation</dt>
                <dd className="text-zinc-950 dark:text-white">
                  Free cancellation until {formatLocalDateTime(preview.fee_refundable_until_local)}
                </dd>
              </>
            )}
            <dt className="font-medium text-zinc-500 dark:text-zinc-400">Approval</dt>
            <dd className="text-zinc-950 dark:text-white">
              {preview.status_if_booked === 'pending'
                ? 'Your management company approves requests for this amenity.'
                : 'Confirmed as soon as you book.'}
            </dd>
          </dl>

          {documentLines.length > 0 && (
            <div className="rounded-lg bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-500/10 dark:text-sky-200">
              <p className="font-medium">This booking needs documents on file:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {documentLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="mt-1">
                You'll upload them from the booking once it's made. It stays awaiting approval
                until your management company accepts them.
              </p>
            </div>
          )}

          {priceChanged && (
            <ErrorAlert>
              The price changed since you started — the fee is now{' '}
              {formatMoney(preview.fee)}. Please check it and confirm again.
            </ErrorAlert>
          )}

          {reviewBlockers.length > 0 && (
            <ErrorAlert title="This booking can't go ahead">
              <ul className="list-disc space-y-1 pl-4">
                {reviewBlockers.map((v) => (
                  <li key={v.code}>{ruleMessage(v.code, amenity)}</li>
                ))}
              </ul>
            </ErrorAlert>
          )}

          {needsAgreement && preview.agreement && (
            <div className="space-y-3">
              <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm text-zinc-800 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700">
                <div className="mb-2 font-semibold">{preview.agreement.title}</div>
                {preview.agreement.body}
              </div>
              <CheckboxField>
                <Checkbox color="sky" checked={agreed} onChange={setAgreed} />
                <Label>I have read and agree to the {preview.agreement.title}</Label>
              </CheckboxField>
            </div>
          )}

          {violations.length > 0 && (
            <ErrorAlert title="Couldn't make that booking">
              <ul className="list-disc space-y-1 pl-4">
                {violations.map((v) => (
                  <li key={v.code}>{ruleMessage(v.code, amenity)}</li>
                ))}
              </ul>
            </ErrorAlert>
          )}

          <ErrorAlert>{error}</ErrorAlert>

          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button
              outline
              onClick={() => {
                setStep('pick');
                setViolations([]);
                setPriceChanged(false);
                setError(null);
              }}
            >
              Back
            </Button>
            <Button
              color="sky"
              onClick={submit}
              disabled={
                working ||
                impersonating ||
                reviewBlockers.length > 0 ||
                (needsAgreement && !agreed)
              }
            >
              {working
                ? 'Booking…'
                : preview.status_if_booked === 'pending'
                  ? 'Send request'
                  : 'Book'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BookingPanel;
